import { useMemo } from 'react';

import { User } from '@/portainer/users/types';
import { Team } from '@/react/portainer/users/teams/types';
import { RbacRole } from '@/react/portainer/users/RolesView/types';
import { useUsers } from '@/portainer/users/queries/useUsers';
import { useTeams } from '@/react/portainer/users/teams/queries';
import { useRbacRoles } from '@/react/portainer/users/RolesView/useRbacRoles';

import { TypedPolicy } from '../../../types';

import {
  KubernetesRbacFormValues,
  KubernetesRbacPolicyPayload,
  KUBERNETES_RBAC_INITIAL_VALUES,
} from './types';

/**
 * Type guard to check if data is KubernetesRbacPolicyPayload
 */
function isKubernetesRbacPolicyPayload(
  data: unknown
): data is KubernetesRbacPolicyPayload {
  return (
    typeof data === 'object' &&
    data !== null &&
    'EnvironmentAccessPolicies' in data
  );
}

/**
 * Hook to get initial values for Kubernetes RBAC Policy form. **Only a Portainer Admin has access to users/teams across all environments.**
 *
 * @param policy - Optional policy to load values from (for edit mode)
 * @returns Form initial values
 */
export function useKubernetesRBACInitialValues(
  policy?: TypedPolicy<unknown>
): KubernetesRbacFormValues {
  const usersQuery = useUsers(false);
  const users = useMemo(() => usersQuery.data || [], [usersQuery.data]);
  const teamsQuery = useTeams();
  const teams = useMemo(() => teamsQuery.data || [], [teamsQuery.data]);
  const rbacRolesQuery = useRbacRoles({
    select: (roles) =>
      roles.filter((role) => role.Scope && role.Scope.kubernetes),
  });
  const roles = useMemo(() => rbacRolesQuery.data || [], [rbacRolesQuery.data]);

  return useMemo(
    () => getKubernetesRBACInitialValuesFromPolicy(users, teams, roles, policy),
    [policy, users, teams, roles]
  );
}

export function getKubernetesRBACInitialValuesFromPolicy(
  users: User[],
  teams: Team[],
  roles: RbacRole[],
  policy?: TypedPolicy<unknown>
): KubernetesRbacFormValues {
  // Create mode: use defaults
  if (!policy || !isKubernetesRbacPolicyPayload(policy.Data)) {
    return KUBERNETES_RBAC_INITIAL_VALUES;
  }

  const { EnvironmentAccessPolicies: envPolicies } = policy.Data;
  if (!envPolicies) {
    return KUBERNETES_RBAC_INITIAL_VALUES;
  }

  const { UserAccessPolicies, TeamAccessPolicies } = envPolicies;

  let idCounter = 1;
  const userAccesses = Object.entries(UserAccessPolicies || {}).flatMap(
    ([userId, policyData]) => {
      const id = parseInt(userId, 10);
      const user = users.find((u) => u.Id === id);
      if (!user) {
        return [];
      }

      const roleId = policyData.value?.RoleId ?? 0;
      const roleName = roles.find((r) => r.Id === roleId)?.Name || '';

      const item = {
        Id: idCounter,
        PartyId: id,
        Type: 'user' as const,
        Name: user.Username,
        Role: { Id: roleId, Name: roleName },
        namespaces: policyData.value?.Namespaces,
      };

      idCounter++;
      return item;
    }
  );

  const teamAccesses = Object.entries(TeamAccessPolicies || {}).flatMap(
    ([teamId, policyData]) => {
      const id = parseInt(teamId, 10);
      const team = teams.find((t) => t.Id === id);
      if (!team) {
        return [];
      }

      const roleId = policyData.value?.RoleId ?? 0;
      const roleName = roles.find((r) => r.Id === roleId)?.Name || '';

      const item = {
        Id: idCounter,
        PartyId: id,
        Type: 'team' as const,
        Name: team.Name,
        Role: { Id: roleId, Name: roleName },
        namespaces: policyData.value?.Namespaces,
      };

      idCounter++;
      return item;
    }
  );

  return {
    name: policy.Name || '',
    environmentGroupIds: policy.EnvironmentGroups || [],
    mode: policy.Data.Mode === 'additive' ? 'additive' : 'least-privilege',
    accessList: [...userAccesses, ...teamAccesses],
  };
}
